
import costOfLivingData from '~/data/costOfLivingData.json'

export default {
  name: 'CostOfLivingTable',
  props: {
    country: {
      type: String,
      default: 'Japan',
    },
    currencies: {
      type: String,
      default: 'JPY,USD,GBP',
    },
    tablestyle: {
      type: String,
      default: 'default',
    }
  },
  data() {
    return {
        loading: true,
        tableHtml: '',
        month: null,
        sections: {
        'Restaurants': [
          'Inexpensive restaurant meal',
          'Domestic beer (0.5 litre)',
          'Regular cappuccino',
          'Water (0.33 litre)'
        ],
        'Markets': [
          'Regular milk (1 litre)',
          'Loaf of white bread',
          'Regular eggs (1 dozen)',
          'Apples (1 kg)'
        ],
        'Transportation': [
          'One-way ticket (local transport)',
          'Monthly pass (regular price)',
          'Taxi start (normal tariff)',
          'Gasoline (1 litre)'
        ],
        'Utilities': [
          'Electricity, heating, cooling, water, and garbage (for a regular apartment)',
          'Regular prepaid monthly mobile tariff with 10GB+ data',
          'Internet (60 Mbps, unlimited data, cable/ADSL)'
        ],
        'Clothing and shoes': [
          'Pair of jeans (Levis 501 or something similar)',
          'Summer dress in a chain store',
          'Nike running shoes (mid-range)',
          'Men\'s leather business shoes'
        ]
      }
    }
  },
  async mounted() {
    const date = new Date();
    this.month = date.toLocaleString('default', { month: 'short' });
    this.year = date.toLocaleString('default', { year: 'numeric' });
    try {
      this.tableHtml = await this.generateTableFromStaticData();
    } catch (error) {
      console.error('Error loading data:', error);
      this.tableHtml = '<div class="error">Error loading data</div>';
    } finally {
      this.loading = false;
    }
  },
  methods: {
    formatCountry(country) {
      return country.split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join('+');
    },

    generateTableFromStaticData() {
        const countryKey = this.country.toLowerCase();
        const countryData = costOfLivingData[countryKey];
        
        if (!countryData) {
            throw new Error(`No data found for country: ${this.country}`);
        }

        const costs = countryData.costs;
        const groupedCosts = costs.reduce((acc, cost) => {
            if (!acc[cost.item]) acc[cost.item] = [];
            acc[cost.item].push(cost);
            return acc;
        }, {});

        const currencies = this.currencies.split(',');
        
        let html = '<table class="cost-table shadow-lg '+this.$props.tablestyle+'"><tbody>';
        
        // Add table rows for each section
        for (const [section, items] of Object.entries(this.sections)) {
            // Add section header with currency columns
            html += '<tr class="section-header">';
            html += `<th class="section-title" style="width: 40%">${section}</th>`;
            currencies.forEach(currency => {
            html += `<th class="currency-header" style="width: ${60/currencies.length}%">${currency}</th>`;
            });
            html += '</tr>';

            // Add items with their costs
            items.forEach(item => {
            html += '<tr class="item-row">';
            html += `<td class="item-name">${item}</td>`;
            currencies.forEach(currency => {
                const costItem = groupedCosts[item]?.find(cost => cost.currency === currency);
                const cost = costItem?.cost || '-';
                html += `<td class="cost-value">${cost}</td>`;
            });
            html += '</tr>';
            });
        }

        html += `</tbody></table><p class="text-right italic !text-sm mt-2">Data sourced from <a rel="nofollow noopener noreferrer" target="_BLANK" href="https://www.numbeo.com/cost-of-living/">Numbeo</a>. Last updated ${this.month} ${this.year}</p>`;
        return html;
    },

    async getData() {
        console.log('Getting cost of living data...')
        const country = this.formatCountry(this.$props.country);
        const currencies = this.$props.currencies ? this.$props.currencies.split(',') : ['GBP'];
        // console.log('COST PROPS',country, currencies)
        let allCosts = [];

        for (const currency of currencies) {
            const formattedCurrency = currency.toUpperCase();
            try {
                const response = await fetch(location.protocol+'//'+location.host +'/.netlify/functions/costs', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        country,
                        currency: formattedCurrency
                    })
                });

                if (!response.ok) {
                    console.error(`Failed to fetch data for ${country} in ${formattedCurrency}`);
                    continue;
                }
                
                const html = await response.text();
                // console.log('Response HTML:', html); // Debug response
                
                const parser = new DOMParser();
                const doc = parser.parseFromString(html, 'text/html');
                
                const rows = Array.from(doc.querySelectorAll('tr'))
                    .filter(row => row.children.length >= 2)
                    .map(row => ({
                        item: row.children[0]?.textContent?.trim() || '',
                        cost: row.children[1]?.textContent?.trim() || '',
                        range: row.children[2]?.textContent?.trim() || ''
                    }));

                // console.log('Processed rows:', rows); // Debug parsed data

                const costs = rows.map(({ item, cost: costWithSymbol, range }) => {
                    if (!costWithSymbol || typeof costWithSymbol !== 'string') {
                        console.warn(`Invalid cost value for ${item}:`, costWithSymbol);
                        return {
                            item,
                            cost: '',
                            range: {
                                low: '',
                                high: '',
                            },
                            currency: formattedCurrency,
                        };
                    }

                    try {
                        const cost = costWithSymbol.replace(/[^0-9.,]/g, '');
                        const [rangeLow, rangeHigh] = (range || '').split('-').map(v => v?.trim() || '');
                        return {
                            item,
                            cost,
                            range: {
                                low: rangeLow,
                                high: rangeHigh,
                            },
                            currency: formattedCurrency,
                        };
                    } catch (err) {
                        console.warn(`Error processing cost for ${item}:`, err);
                        return {
                            item,
                            cost: '',
                            range: {
                                low: '',
                                high: '',
                            },
                            currency: formattedCurrency,
                        };
                    }
                });

                console.log('Processed costs:', formattedCurrency); // Debug processed costs
                allCosts = [...allCosts, ...costs];
            } catch (error) {
                console.error('Error fetching cost of living data:', error);
                continue;
            }
        }

        // Mapping of original item text to required item text
        const keywordMapping = {
            'Inexpensive Restaurant': 'Inexpensive restaurant meal',
            'Domestic Beer': 'Domestic beer (0.5 litre)',
            'Cappuccino': 'Regular cappuccino',
            'Water': 'Water (0.33 litre)',
            'Milk (regular)': 'Regular milk (1 litre)',
            'White Bread': 'Loaf of white bread',
            'Eggs': 'Regular eggs (1 dozen)',
            'Apples': 'Apples (1 kg)',
            'One-way': 'One-way ticket (local transport)',
            'Monthly Pass': 'Monthly pass (regular price)',
            'Taxi Start': 'Taxi start (normal tariff)',
            'Gasoline': 'Gasoline (1 litre)',
            'Basic (Electricity': 'Electricity, heating, cooling, water, and garbage (for a regular apartment)',
            'Mobile': 'Regular prepaid monthly mobile tariff with 10GB+ data',
            'Internet': 'Internet (60 Mbps, unlimited data, cable/ADSL)',
            'Jeans': 'Pair of jeans (Levis 501 or something similar)',
            'Summer Dress': 'Summer dress in a chain store',
            'Nike': 'Nike running shoes (mid-range)',
            'Business Shoes': 'Men\'s leather business shoes'
        };

        allCosts = allCosts
        .map(cost => {
            
            // Special case for utilities since it contains words that could match other items
            if (cost.item.includes('Basic (Electricity')) {
                return {
                    ...cost,
                    item: 'Electricity, heating, cooling, water, and garbage (for a regular apartment)'
                };
            }
            
            // Find the first keyword that matches, excluding utilities case
            const matchedKey = Object.keys(keywordMapping).find(key => {
                // Skip the utilities mapping since we handled it above
                if (key === 'Basic (Electricity') return false;
                
                // For other items, ensure we're not matching part of a larger word
                const words = cost.item.toLowerCase().split(/[\s,()]+/);
                const keyWords = key.toLowerCase().split(/[\s,()]+/);
                
                const matches = keyWords.every(keyWord => 
                    words.includes(keyWord)
                );
                
                return matches;
            });
            
            return matchedKey ? {
                ...cost,
                item: keywordMapping[matchedKey]
            } : null;
        })
        .filter(Boolean);

        return this.generateHtmlTable(allCosts);
    },

    chunkArray(arr, chunkSize) {
      return Array.from({ length: Math.ceil(arr.length / chunkSize) }, (_, i) =>
        arr.slice(i * chunkSize, (i + 1) * chunkSize)
      );
    },


    generateHtmlTable(costs) {
        const groupedCosts = costs.reduce((acc, cost) => {
            if (!acc[cost.item]) acc[cost.item] = [];
            acc[cost.item].push(cost);
            return acc;
        }, {});

        const currencies = [...new Set(costs.map(cost => cost.currency))];
        
        let html = `<table class="cost-table shadow-lg `+this.$props.tablestyle+`"><tbody>`;
        
        // Add table rows for each section
        for (const [section, items] of Object.entries(this.sections)) {
            // Add section header with currency columns
            html += `<tr class="section-header">`;
            html += `<th class="section-title" style="width: 40%">${section}</th>`;
            currencies.forEach(currency => {
                html += `<th class="currency-header" style="width: ${60/currencies.length}%">${currency}</th>`;
            });
            html += '</tr>';

            // Add items with their costs
            items.forEach(item => {
                html += `<tr class="item-row">`;
                html += `<td class="item-name">${item}</td>`;
                currencies.forEach(currency => {
                    const costItem = groupedCosts[item]?.find(cost => cost.currency === currency);
                    const cost = costItem?.cost || '-';
                    html += `<td class="cost-value">${cost}</td>`;
                });
                html += '</tr>';
            });
        }

        html += '</tbody></table><p class="text-right italic !text-sm mt-2">Data sourced from <a rel="nofollow noopener noreferrer" target="_BLANK" href="https://www.numbeo.com/cost-of-living/">Numbeo</a>. Last updated '+this.month+' '+this.year+'</p>';
        return html;
    }
    },
}
